<script setup lang="ts">
    import { ref, computed, watch } from 'vue'
    import { Input } from '@/components/ui/input'
    import { cn } from '@/lib/utils'
    import { onClickOutside } from '@vueuse/core'
    
    const props = defineProps<{
      modelValue: string
      options: string[]
      placeholder?: string
      class?: string
      required?: boolean
      id?: string
    }>()
    
    const emit = defineEmits<{
      'update:modelValue': [value: string]
    }>()
    
    const isOpen = ref(false)
    const searchQuery = ref('')
    const containerRef = ref<HTMLElement>()
    
    // Filter options based on search query
    const filteredOptions = computed(() => {
      if (!searchQuery.value) return props.options
      const query = searchQuery.value.toLowerCase()
      return props.options.filter(option => 
        option.toLowerCase().includes(query)
      )
    })
    
    // Update search query when modelValue changes externally
    watch(() => props.modelValue, (newValue) => {
      searchQuery.value = newValue || ''
    })
    
    // Initialize search query with current value
    searchQuery.value = props.modelValue || ''
    
    // Handle input change
    function onInput(event: Event) {
      const target = event.target as HTMLInputElement
      searchQuery.value = target.value
      isOpen.value = true
      emit('update:modelValue', target.value)
    }
    
    // Handle option selection
    function selectOption(option: string) {
      searchQuery.value = option
      emit('update:modelValue', option)
      isOpen.value = false
    }
    
    // Handle input focus
    function onFocus() {
      isOpen.value = true
    }
    
    // Close dropdown when clicking outside
    onClickOutside(containerRef, () => {
      isOpen.value = false
      // Reset search query to current modelValue if dropdown closes without selection
      if (props.modelValue !== searchQuery.value) {
        searchQuery.value = props.modelValue || ''
      }
    })
    </script>
    
    <template>
      <div ref="containerRef" class="relative w-full">
        <Input
          :id="id"
          :model-value="searchQuery"
          @input="onInput"
          @focus="onFocus"
          :placeholder="placeholder"
          :class="cn('w-full', props.class)"
          :required="required"
        />
        <div
          v-if="isOpen && filteredOptions.length > 0"
          class="absolute z-50 w-full mt-1 bg-white border border-input rounded-md shadow-lg max-h-60 overflow-auto"
        >
          <div
            v-for="option in filteredOptions"
            :key="option"
            class="px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground text-sm"
            :class="{
              'bg-accent text-accent-foreground': option === modelValue
            }"
            @click="selectOption(option)"
          >
            {{ option }}
          </div>
        </div>
        <div
          v-if="isOpen && filteredOptions.length === 0 && searchQuery"
          class="absolute z-50 w-full mt-1 bg-white border border-input rounded-md shadow-lg"
        >
          <div class="px-3 py-2 text-sm text-muted-foreground">
            Keine Ergebnisse gefunden
          </div>
        </div>
      </div>
    </template>
    
    